import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MessageSquare, FolderKanban, Cpu, Activity, FileText, Library, Layers, Shield,
  PanelLeftClose, PanelLeftOpen, LogOut, Sparkles, Home, Waypoints,
  Settings as SettingsIcon, ShieldAlert, ChevronRight,
} from "lucide-react";
import { LOGO_ICON } from "@/config/dashboardConstants";
import ThemeToggle from "@/components/ThemeToggle";
import ChatArea from "@/components/workspace/ChatArea";
import DecisionGraphArea from "@/components/workspace/DecisionGraphArea";
import RailProjects from "@/components/workspace/RailProjects";
import RailChats from "@/components/workspace/RailChats";
import {
  ProjectsArea, OptimizationArea, ReportsArea, RunsArea, KnowledgeArea, AdminArea, ModulesArea,
  SettingsArea, PlatformAdminArea,
} from "@/components/workspace/WorkspaceAreas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed, audited component registry. The backend returns ONLY area identifiers;
// the frontend maps them here. Server-supplied routes/components are never executed.
const AREA_REGISTRY = {
  chat: ChatArea,
  projects: ProjectsArea,
  optimization: OptimizationArea,
  runs: RunsArea,
  "decision-graph": DecisionGraphArea,
  reports: ReportsArea,
  knowledge: KnowledgeArea,
  modules: ModulesArea,
  admin: AdminArea,
  settings: SettingsArea,
  "platform-admin": PlatformAdminArea,
};

const ICONS = {
  "message-square": MessageSquare, "folder-kanban": FolderKanban, cpu: Cpu,
  activity: Activity, "file-text": FileText, library: Library, layers: Layers, shield: Shield,
  waypoints: Waypoints, settings: SettingsIcon, "shield-alert": ShieldAlert,
};

export default function AppShell() {
  const navigate = useNavigate();
  const { area = "chat", sub } = useParams();
  const [nav, setNav] = useState(null);
  const [err, setErr] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem("qn-rail-expanded")) || { chat: true, projects: true }; }
    catch { return { chat: true, projects: true }; }
  });
  useEffect(() => {
    try { localStorage.setItem("qn-rail-expanded", JSON.stringify(expanded)); } catch { /* ignore */ }
  }, [expanded]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workspace/nav`);
      setNav(data);
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/login?return_to=/app/chat"); return; }
      setErr("Could not load your workspace.");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Redirect to Chat when the requested area is not permitted for this user.
  useEffect(() => {
    if (!nav) return;
    const ids = new Set(nav.areas.map((a) => a.id));
    if (area && !ids.has(area)) navigate("/app/chat", { replace: true });
  }, [nav, area, navigate]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    navigate("/login");
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
        <div className="text-[var(--qn-foreground-muted)]" data-testid="workspace-error">{err}</div>
      </div>
    );
  }
  if (!nav) {
    return (
      <div className="min-h-screen bg-[var(--qn-background)] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allowedIds = new Set(nav.areas.map((a) => a.id));
  const activeId = allowedIds.has(area) ? area : "chat";
  const ActiveComponent = AREA_REGISTRY[activeId] || ChatArea;
  const c = collapsed;

  return (
    <div className="min-h-screen bg-[var(--qn-background)] text-[var(--qn-foreground)] flex">
      {/* Left rail — derived entirely from the nav endpoint */}
      <motion.aside
        initial={false}
        animate={{ width: c ? 76 : 248 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="fixed left-0 top-0 bottom-0 z-40 flex flex-col border-r border-[var(--qn-border)] bg-[var(--qn-card)] overflow-hidden"
        data-testid="app-shell-sidebar"
      >
        <div className={`flex items-center ${c ? "justify-center" : "justify-between"} px-4 py-4 border-b border-[var(--qn-border)]`}>
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate("/app/chat")}>
            <img src={LOGO_ICON} alt="QNNEAL" className={`${c ? "h-8 w-8" : "h-9 w-9"} object-contain`} />
            {!c && <span className="font-outfit font-bold text-xl tracking-tight">QNNEAL</span>}
          </div>
          <button onClick={() => setCollapsed(!c)} data-testid="shell-collapse-btn"
            className="w-7 h-7 rounded-md flex items-center justify-center text-[var(--qn-foreground-muted)] hover:bg-[var(--qn-surface-hover)]">
            {c ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        {!c && (
          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-[var(--qn-foreground-subtle)] font-semibold">
            Workspace
          </div>
        )}
        <nav className={`flex-1 ${c ? "px-2" : "px-3"} py-2 space-y-1 overflow-y-auto`} data-testid="shell-nav">
          {nav.areas.filter((a) => !a.hidden).map((a) => {
            const Icon = ICONS[a.icon] || Home;
            const hasTree = (a.id === "chat" || a.id === "projects") && !c;
            const isOpen = expanded[a.id];
            // Hidden areas (Decision Graph) are reached from the Optimization catalog → light up that rail item.
            const isActive = a.id === activeId || (a.id === "optimization" && nav.areas.some((h) => h.hidden && h.id === activeId));
            const rowClass = `w-full flex items-center ${c ? "justify-center px-2" : "px-3"} gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-[var(--qn-primary-muted)] text-[var(--qn-primary)] border-l-2 border-[var(--qn-primary)]"
                : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]"}`;
            return (
              <React.Fragment key={a.id}>
                <div className={`flex items-center ${rowClass} !py-0`} title={c ? a.label : undefined}>
                  <button data-testid={`shell-area-${a.id}`}
                    onClick={() => navigate(`/app/${a.id}`)}
                    className={`flex-1 min-w-0 flex items-center ${c ? "justify-center" : ""} gap-3 py-2.5 text-left`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    {!c && <span className="truncate">{a.label}</span>}
                  </button>
                  {hasTree && (
                    <button data-testid={`shell-area-chevron-${a.id}`} aria-label={`${isOpen ? "Collapse" : "Expand"} ${a.label}`}
                      onClick={(e) => { e.stopPropagation(); setExpanded((s) => ({ ...s, [a.id]: !s[a.id] })); }}
                      className="w-6 h-6 -mr-1 rounded-md flex items-center justify-center text-[var(--qn-foreground-subtle)] hover:bg-[var(--qn-surface-hover)] shrink-0">
                      <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </button>
                  )}
                </div>
                {a.id === "chat" && isOpen && <RailChats collapsed={c} />}
                {a.id === "projects" && isOpen && <RailProjects collapsed={c} />}
              </React.Fragment>
            );
          })}

          {nav.modules?.length > 0 && (
            <>
              <div className="border-t border-[var(--qn-border)] my-3" />
              {!c && (
                <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-[var(--qn-foreground-subtle)] font-semibold">
                  Installed Modules
                </div>
              )}
              {nav.modules.map((m) => (
                <button key={m.module_id} data-testid={`shell-module-${m.module_id}`}
                  onClick={() => navigate("/app/optimization")}
                  title={c ? m.label : undefined}
                  className={`w-full flex items-center ${c ? "justify-center px-2" : "px-3"} gap-3 py-2 rounded-lg text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]`}>
                  <Layers className="w-4 h-4 shrink-0" />
                  {!c && <span className="truncate">{m.label}</span>}
                </button>
              ))}
            </>
          )}
        </nav>

        <div className={`border-t border-[var(--qn-border)] ${c ? "p-2" : "p-3"} space-y-2`}>
          <div className={`flex items-center ${c ? "flex-col gap-2" : "justify-between"} `}>
            {!c && (
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" data-testid="shell-user-name">{nav.identity.name || "You"}</div>
                <div className="text-[11px] text-[var(--qn-foreground-subtle)] truncate">{nav.identity.role}</div>
              </div>
            )}
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <button onClick={logout} data-testid="shell-logout"
                className="w-8 h-8 rounded-md flex items-center justify-center text-[var(--qn-foreground-subtle)] hover:text-red-400 hover:bg-[var(--qn-surface-hover)]">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.aside>

      {/* Main */}
      <main className="flex-1 min-h-screen" style={{ marginLeft: c ? 76 : 248 }} data-testid="app-shell-main">
        <div className="h-full">
          <ActiveComponent identity={nav.identity} sub={sub} nav={nav} />
        </div>
      </main>
    </div>
  );
}
